import { useEffect } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';

export const EMERGENCY_CONTACT_REVIEW_MONTHS = 12;
export const NEW_ACCOUNT_EMERGENCY_CONTACT_GRACE_DAYS = 30;
export const EMERGENCY_CONTACT_REMINDER_COOLDOWN_DAYS = 30;

function resolveDate(value) {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

export function isStale(value, now = new Date()) {
  const lastModified = resolveDate(value);
  if (!lastModified) return true;

  const monthsSince =
    (now.getFullYear() - lastModified.getFullYear()) * 12 +
    (now.getMonth() - lastModified.getMonth());
  return monthsSince >= EMERGENCY_CONTACT_REVIEW_MONTHS;
}

function daysSince(value, now = new Date()) {
  const date = resolveDate(value);
  if (!date) return null;
  return (now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000);
}

export function getEmergencyContactReminderMessage(privateData = {}, userCreationTime = null, now = new Date()) {
  const createdAt = privateData?.createdAt || userCreationTime;
  const hasEmergencyContacts = Array.isArray(privateData?.emergencyContacts) && privateData.emergencyContacts.length > 0;

  if (!hasEmergencyContacts) {
    const accountAgeDays = daysSince(createdAt, now);
    if (accountAgeDays !== null && accountAgeDays < NEW_ACCOUNT_EMERGENCY_CONTACT_GRACE_DAYS) return '';
    return 'Add some emergency contacts so your clubs know who to contact if needed.';
  }

  const reviewDate = privateData?.emergencyContactsLastReviewed
    || privateData?.emergencyContactsLastModified
    || createdAt;
  return isStale(reviewDate, now)
    ? 'It’s been over 12 months since you updated your emergency contacts. Please review them.'
    : '';
}

export function shouldSkipRecentlyPromptedReminder(privateData = {}, hasExistingReminder = false, now = new Date()) {
  if (hasExistingReminder) return false;
  const promptedAgeDays = daysSince(privateData?.emergencyContactsReminderLastPrompted, now);
  return promptedAgeDays !== null && promptedAgeDays < EMERGENCY_CONTACT_REMINDER_COOLDOWN_DAYS;
}

export default function EmergencyContactReminder() {
  const { user } = useAuth();
  const userId = user?.uid || '';
  const userCreationTime = user?.metadata?.creationTime || null;

  useEffect(() => {
    const checkEmergencyContactStale = async () => {
      if (!userId) return;

      const privateSnap = await getDoc(doc(db, 'privateUsers', userId));
      if (!privateSnap.exists()) return;

      const privateRef = doc(db, 'privateUsers', userId);
      const privateData = privateSnap.data() || {};
      const reminderMessage = getEmergencyContactReminderMessage(privateData, userCreationTime);
      const existingQuery = query(
        collection(db, 'notifications', userId, 'items'),
        where('type', '==', 'reminder-emergency-check')
      );
      const existingSnap = await getDocs(existingQuery);

      if (!reminderMessage) {
        if (!existingSnap.empty) {
          await Promise.all(existingSnap.docs.map((snap) => deleteDoc(snap.ref).catch(() => {})));
        }
        return;
      }

      if (shouldSkipRecentlyPromptedReminder(privateData, !existingSnap.empty)) return;

      if (!existingSnap.empty) {
        await Promise.all(existingSnap.docs.map((snap) => setDoc(snap.ref, {
          message: reminderMessage,
          actionUrl: '/profile?tab=emergency',
          notificationCategory: 'family_profile',
        }, { merge: true }).catch(() => {})));
        return;
      }

      const notificationRef = doc(db, 'notifications', userId, 'items', crypto.randomUUID());
      await setDoc(notificationRef, {
        type: 'reminder-emergency-check',
        notificationCategory: 'family_profile',
        message: reminderMessage,
        actionUrl: '/profile?tab=emergency',
        read: false,
        timestamp: serverTimestamp(),
        emailed: false,
      });
      await setDoc(privateRef, {
        emergencyContactsReminderLastPrompted: serverTimestamp(),
      }, { merge: true });
    };

    checkEmergencyContactStale();
  }, [userCreationTime, userId]);

  return null; // invisible logic-only component
}
